import { z } from 'zod';

/**
 * The requester snapshot a family sends with a Connect request (steward-app#1303), and the ONE
 * definition of its bounds for both ends of the wire.
 *
 * ⚠️ **A MIRROR of appcore's `EngagementRequesterSchema`
 * (`packages/core/src/entities/engagement.ts`, appcore#780, read 2026-09-29 at main 513e37b+),
 * not a source of truth.** appcore is the validating authority and refuses with a 400; this copy
 * exists so the façade refuses first (nothing is forwarded, no engagement pair is written) and so
 * the SPA can decide what it may show and send with the same rules. If the two ever disagree, the
 * façade must be the STRICTER one: a value this accepts and appcore refuses turns a connect into
 * an upstream 400, while the reverse merely drops a field. `connectRequester.test.ts` pins the
 * caps and the character set, but it cannot see appcore's file, so a change over there needs a
 * matching change here by hand.
 *
 * ⚠️ **FAMILY-SUPPLIED, UNVERIFIED, CROSS-TENANT TEXT.** It is a label for a human at the firm,
 * never proof of who the family is, and no CODE may make a decision from it (an invite target,
 * an address to mail, a lookup key). It is never derived server-side: the only source is the
 * request body, which the SPA fills from the values its Connect review step displays.
 *
 * A person at the firm does decide from it, which is why the façade's `connect` refuses an
 * `email` that is not the one on the caller's own profile (steward-app#1946; see
 * `assertRequesterEmailIsCallers` in `handlers/advisor.ts`). That check is the handler's, not
 * this schema's: the schema cannot know who is calling. That handler compares `name` and
 * `workspaceName` with nothing the server knows about the caller; they are bounded here and
 * otherwise free text.
 *
 * Bounds are refusals, never truncation: a silently shortened name is a different name.
 */
export const REQUESTER_NAME_MAX = 200;
export const REQUESTER_WORKSPACE_NAME_MAX = 200;
export const REQUESTER_EMAIL_MAX = 254;

/** Same character set appcore refuses (C0/C1 controls, zero-width and bidi-override ranges). */
const UNSAFE_REQUESTER_CHARS =
  /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u2064\ufeff]/;

const safeText = (max: number) =>
  z.string().trim().min(1).max(max).refine((s) => !UNSAFE_REQUESTER_CHARS.test(s), {
    message: 'must not contain control, bidi-override or zero-width characters',
  });

export const ConnectRequesterSchema = z.strictObject({
  name: safeText(REQUESTER_NAME_MAX).optional(),
  email: z.string().trim().email().max(REQUESTER_EMAIL_MAX)
    .refine((s) => !UNSAFE_REQUESTER_CHARS.test(s), { message: 'must not contain control characters' })
    .optional(),
  workspaceName: safeText(REQUESTER_WORKSPACE_NAME_MAX).optional(),
});
export type ConnectRequester = z.infer<typeof ConnectRequesterSchema>;

/**
 * The SPA's single decision about what a family is shown AND sends: each candidate value is
 * kept only if the schema above would accept it, else dropped. The Connect review step renders
 * the returned object and sends the same object, so "what is shown is what is sent" holds by
 * construction, and a value appcore would refuse (an over-long workspace name) is neither shown
 * nor sent rather than turning the whole connect into a 400.
 *
 * Returns `undefined` when nothing survives, so the request omits `requester` entirely.
 */
export function requesterFromCandidates(candidates: {
  name?: string | null;
  email?: string | null;
  workspaceName?: string | null;
}): ConnectRequester | undefined {
  const out: ConnectRequester = {};
  for (const key of ['name', 'email', 'workspaceName'] as const) {
    const raw = candidates[key];
    if (typeof raw !== 'string') continue;
    const one = ConnectRequesterSchema.safeParse({ [key]: raw });
    const value = one.success ? one.data[key] : undefined;
    if (value !== undefined) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
